import { useMutation } from '@apollo/client'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import FormControl from '@mui/material/FormControl'
import FormHelperText from '@mui/material/FormHelperText'
import Input from '@mui/material/Input'
import InputLabel from '@mui/material/InputLabel'
import ListItemText from '@mui/material/ListItemText'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import TextField from '@mui/material/TextField'
import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import * as React from 'react'
import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import AsyncButton from '../../components/AsyncButton'
import GraphqlError from '../../components/GraphqlError'
import {
  AttendeeMutationMutation,
  AttendeeMutationMutationVariables,
  attendeesQuery,
  AttendeeType,
  EventAttendeeDataFragment,
  EventAttendeeInput,
  ReminderItemInput,
} from '../../generated/graphql'
import GqlHelper from '../../helpers/GqlHelper'
// import { getAllLanguagesFromISO } from '../../helpers/Languages'
import { TimeZones } from '../../helpers/Locale'
import EventAttendees from '../../schema/EventAttendees'

// Reminder configurations for invitation acceptance reminders
const REMINDER_CONFIGS: Record<string, ReminderItemInput[]> = {
  NONE: [],
  THREE_1M: [{ offset: 60 }, { offset: 120 }, { offset: 180 }],
  THREE_24H: [{ offset: 86400 }, { offset: 172800 }, { offset: 259200 }], // 1 day, 2 days, 3 days
  THREE_48H: [{ offset: 172800 }, { offset: 345600 }, { offset: 518400 }], // 2 days, 4 days, 6 days
  THREE_7D: [{ offset: 604800 }, { offset: 1209600 }, { offset: 1814400 }], // 7 days, 14 days, 21 days
}

interface Props {
  open: boolean
  eventAttendee?: EventAttendeeDataFragment
  eventId: string
  language?: string

  onClose(): void
}

function getInitialState(props: Props, forceReset = false): EventAttendeeInput {
  if (props.eventAttendee && !forceReset) {
    return {
      id: props.eventAttendee.id,
      name: props.eventAttendee.profile.name,
      type: props.eventAttendee.type,
      eventId: props.eventAttendee.event.id,
      message: props.eventAttendee.message,
      email: props.eventAttendee.profile.email,
      language: props.language,
    }
  }
  return {
    type: AttendeeType.Invited,
    eventId: props.eventId,
    message: '',
    email: '',
    name: '',
    language: props.language,
  }
}

const EventAttendeeDialog: React.FC<Props> = (props) => {
  const { open, onClose } = props
  const theme = useTheme()
  const { t } = useTranslation()
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'))
  // eslint-disable-next-line
  const [mutateAttendee, { loading, error }] = useMutation<AttendeeMutationMutation, AttendeeMutationMutationVariables>(
    EventAttendees.EDIT_ATTENDEE,
    {
      update: GqlHelper.getMutationReducer<AttendeeMutationMutation, attendeesQuery>(
        'mutateEventAttendee',
        EventAttendees.GET_ATTENDEES,
        'eventAttendees',
        { eventId: props.eventId },
      ),
    },
  )

  const editAttendee = async (input: EventAttendeeInput) => {
    await mutateAttendee({ variables: { input } })
  }
  const [state, setState] = useState(getInitialState(props))
  const [reminderConfig, setReminderConfig] = useState<string>('NONE')

  // Reset reminder config to NONE when de-DE is selected (privacy mode)
  useEffect(() => {
    if (state.language === 'de-DE') {
      setReminderConfig('NONE')
    }
  }, [state.language])

  const createEventType = async () => {
    try {
      // add the reminder configuration to the attendee input
      const attendeeInput = {
        ...state,
        invitationReminders: REMINDER_CONFIGS[reminderConfig],
      }

      await editAttendee(attendeeInput)

      setState(getInitialState(props, true))
      onClose()
    } catch (e) {
      console.error(e)
    }
  }

  const close = () => {
    setState(getInitialState(props, true))
    onClose()
  }
  // const allLanguages = useMemo(() => getAllLanguagesFromISO(), [])
  const allLanguages = TimeZones

  return (
    <Dialog open={open} onClose={close} aria-labelledby="form-dialog-title" maxWidth="md" fullScreen={fullScreen}>
      <DialogTitle id="form-dialog-title">
        {props.eventAttendee ? t('Edit event attendee') : t('Add new attendee')}
      </DialogTitle>
      <DialogContent>
        <TextField
          variant="standard"
          label={t('Attendee Name')}
          placeholder={t('Name of Attendee')}
          type="text"
          value={state.name || ''}
          onChange={(e) => setState({ ...state, name: e.target.value })}
          fullWidth
        />
        <TextField
          variant="standard"
          label={t('Attendee Email')}
          placeholder={t('Email to which we will send the invitation')}
          type="email"
          value={state.email || ''}
          onChange={(e) => setState({ ...state, email: e.target.value })}
          fullWidth
        />
        <FormControl fullWidth>
          <InputLabel htmlFor="select-multiple-checkbox">{t('Timezone')}</InputLabel>
          <Select
            variant="standard"
            value={state.language || ''}
            onChange={(event) => setState({ ...state, language: event.target.value as string })}
            input={<Input id="select-multiple-checkbox" />}
            displayEmpty={true}
            renderValue={(selected) => {
              const language = allLanguages.find((locale) => locale.code === selected)
              return language ? `${language.timezone}` : ''
            }}
          >
            {allLanguages.map((language) => (
              <MenuItem key={language.code} value={language.code}>
                <ListItemText primary={`${language.timezone}`} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth style={{ marginTop: 16 }} disabled={state.language === 'de-DE'}>
          <InputLabel htmlFor="invitation-reminders">{t('Invitation Reminders')}</InputLabel>
          <Select
            variant="standard"
            value={reminderConfig}
            onChange={(event) => setReminderConfig(event.target.value as string)}
            input={<Input id="invitation-reminders" />}
          >
            <MenuItem value="NONE">
              <ListItemText primary={t('No reminders')} secondary={t("Don't send invitation reminders")} />
            </MenuItem>
            <MenuItem value="THREE_1M">
              <ListItemText
                primary={t('3 reminders, every 1 minute')}
                secondary={t('Sent 1, 2, and 3 minute after invitation')}
              />
            </MenuItem>
            <MenuItem value="THREE_24H">
              <ListItemText
                primary={t('3 reminders, every 24 hours')}
                secondary={t('Sent 1, 2, and 3 days after invitation')}
              />
            </MenuItem>
            <MenuItem value="THREE_48H">
              <ListItemText
                primary={t('3 reminders, every 48 hours')}
                secondary={t('Sent 2, 4, and 6 days after invitation')}
              />
            </MenuItem>
            <MenuItem value="THREE_7D">
              <ListItemText
                primary={t('3 reminders, every 7 days')}
                secondary={t('Sent 7, 14 and 21 days after invitation')}
              />
            </MenuItem>
          </Select>
          {state.language === 'de-DE' && (
            <FormHelperText>{t('Reminders are disabled for Germany due to privacy mode')}</FormHelperText>
          )}
        </FormControl>
        {/* <TextField
          label={t('Message')}
          placeholder={t('Write a custom notification message')}
          type="text"
          multiline
          value={state.message || ''}
          onChange={(e) => setState({ ...state, message: e.target.value })}
          fullWidth
        /> */}
        <GraphqlError error={error} />
      </DialogContent>
      <DialogActions>
        <Button onClick={close} color="primary">
          {t('Cancel')}
        </Button>
        <AsyncButton onClick={createEventType} disabled={!state.email} />
      </DialogActions>
    </Dialog>
  )
}

export default EventAttendeeDialog
